import { useCallback, useReducer } from 'react';

export type SelectionState = {
  productId: string | null;
  optionId: string | null;
  price: number;
  quantity: number;
  includeDeliveryFee: boolean;
  selectedDeliveryFeeOptionIndex: number;
};

type SelectionAction =
  | { type: 'SELECT'; productId: string; optionId: string | null; price: number }
  | { type: 'INCREASE_QUANTITY'; productId: string }
  | { type: 'DECREASE_QUANTITY'; productId: string }
  | { type: 'TOGGLE_DELIVERY_FEE' }
  | { type: 'SELECT_DELIVERY_FEE_OPTION'; index: number }
  | { type: 'RESET' };

const initialState: SelectionState = {
  productId: null,
  optionId: null,
  price: 0,
  quantity: 0,
  includeDeliveryFee: false,
  selectedDeliveryFeeOptionIndex: 0,
};

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SELECT':
      return {
        productId: action.productId,
        optionId: action.optionId,
        price: action.price,
        quantity: 1,
        includeDeliveryFee: false,
        selectedDeliveryFeeOptionIndex: 0,
      };
    case 'INCREASE_QUANTITY':
      if (state.productId !== action.productId) return state;
      return { ...state, quantity: state.quantity + 1 };
    case 'DECREASE_QUANTITY':
      if (state.productId !== action.productId) return state;
      return state.quantity <= 1 ? initialState : { ...state, quantity: state.quantity - 1 };
    case 'TOGGLE_DELIVERY_FEE':
      return { ...state, includeDeliveryFee: !state.includeDeliveryFee };
    case 'SELECT_DELIVERY_FEE_OPTION':
      return { ...state, selectedDeliveryFeeOptionIndex: action.index };
    case 'RESET':
      return initialState;
  }
}

function useChatOrderSelection() {
  const [state, dispatch] = useReducer(selectionReducer, initialState);

  const select = useCallback(
    (params: { productId: string; optionId: string | null; price: number }) => {
      dispatch({ type: 'SELECT', ...params });
    },
    []
  );

  const increaseQuantity = useCallback((productId: string) => {
    dispatch({ type: 'INCREASE_QUANTITY', productId });
  }, []);

  const decreaseQuantity = useCallback((productId: string) => {
    dispatch({ type: 'DECREASE_QUANTITY', productId });
  }, []);

  const toggleDeliveryFee = useCallback(() => {
    dispatch({ type: 'TOGGLE_DELIVERY_FEE' });
  }, []);

  const selectDeliveryFeeOption = useCallback((index: number) => {
    dispatch({ type: 'SELECT_DELIVERY_FEE_OPTION', index });
  }, []);

  const isSubmitDisabled = state.productId === null || state.price === 0 || state.quantity <= 0;

  return {
    state,
    select,
    increaseQuantity,
    decreaseQuantity,
    toggleDeliveryFee,
    selectDeliveryFeeOption,
    isSubmitDisabled,
  };
}

export default useChatOrderSelection;
